import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, Wrench, AlertTriangle } from 'lucide-react';

import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';

import { ejecutarMantenimiento, type EjecutarMantenimientoPayload } from '@/services/api/mantenimiento.api';
import { getApiErrorMessage } from '@/services/api/client';
import type { MantenimientoItem } from '@/types/dominio';

interface Props {
  item: MantenimientoItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EjecutarMantenimientoDialog({ item, open, onOpenChange }: Props) {
  const qc = useQueryClient();
  const [tipo, setTipo] = useState<'MANTENIMIENTO_PREVENTIVO' | 'MANTENIMIENTO_CORRECTIVO'>(
    'MANTENIMIENTO_PREVENTIVO',
  );
  const [observaciones, setObservaciones] = useState('');
  const [periodicidad, setPeriodicidad] = useState<string>(
    item?.periodicidad_mto_dias?.toString() ?? '180',
  );
  const [pdfFile, setPdfFile] = useState<File | null>(null);

  const mutation = useMutation({
    mutationFn: (payload: EjecutarMantenimientoPayload) =>
      ejecutarMantenimiento(item!.id_item_maestro, payload),
    onSuccess: () => {
      toast.success('Mantenimiento registrado', {
        description: `Se actualizó el activo ${item?.serial_global}.`,
      });
      qc.invalidateQueries({ queryKey: ['mantenimientos'] });
      qc.invalidateQueries({ queryKey: ['inventario-dashboard'] });
      onOpenChange(false);
      setObservaciones('');
      setPdfFile(null);
    },
    onError: (err) => {
      toast.error('No se pudo registrar el mantenimiento', {
        description: getApiErrorMessage(err),
      });
    },
  });

  if (!item) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary-50 text-primary-700">
              <Wrench className="h-4 w-4" />
            </span>
            Ejecutar mantenimiento
          </DialogTitle>
          <DialogDescription>
            Registra el mantenimiento del activo. Se actualizará la fecha del próximo y se guardará
            en el historial.
          </DialogDescription>
        </DialogHeader>

        {/* Resumen del activo */}
        <div className="rounded-lg border bg-muted/30 p-3 text-sm space-y-1">
          <div className="flex justify-between gap-3">
            <span className="text-muted-foreground">Serial</span>
            <span className="font-mono font-semibold">{item.serial_global}</span>
          </div>
          {item.nombre_glpi && (
            <div className="flex justify-between gap-3">
              <span className="text-muted-foreground">Activo</span>
              <span className="font-medium">{item.nombre_glpi}</span>
            </div>
          )}
          {item.empleado_asignado && (
            <div className="flex justify-between gap-3">
              <span className="text-muted-foreground">Asignado a</span>
              <span className="font-medium truncate max-w-[260px]">{item.empleado_asignado.nombres}</span>
            </div>
          )}
          {item.fecha_ultimo_mto && (
            <div className="flex justify-between gap-3">
              <span className="text-muted-foreground">Último mantenimiento</span>
              <span>{new Date(item.fecha_ultimo_mto).toLocaleDateString('es-CO')}</span>
            </div>
          )}
        </div>

        {item.semaforo === 'DANGER' && (
          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>Este activo ya tiene el mantenimiento vencido. Será marcado como al día tras esta operación.</span>
          </div>
        )}

        <div className="grid gap-4">
          <div className="grid gap-1.5">
            <Label htmlFor="tipo">Tipo de mantenimiento</Label>
            <Select value={tipo} onValueChange={(v) => setTipo(v as typeof tipo)}>
              <SelectTrigger id="tipo">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="MANTENIMIENTO_PREVENTIVO">Preventivo</SelectItem>
                <SelectItem value="MANTENIMIENTO_CORRECTIVO">Correctivo</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="periodicidad">Próximo mantenimiento (días)</Label>
            <Input
              id="periodicidad"
              type="number"
              min={1}
              max={3650}
              value={periodicidad}
              onChange={(e) => setPeriodicidad(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Se programará el próximo en <span className="font-medium tabular">{periodicidad || 0}</span> días.
            </p>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="observaciones">Observaciones (opcional)</Label>
            <Textarea
              id="observaciones"
              placeholder="Reemplazo de pasta térmica, limpieza interior, validación de batería..."
              value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)}
              rows={4}
            />
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="pdf">Archivo PDF (obligatorio)</Label>
            <Input
              id="pdf"
              type="file"
              accept="application/pdf"
              onChange={(e) => setPdfFile(e.target.files?.[0] || null)}
            />
            <p className="text-xs text-muted-foreground">
              Sube el comprobante o reporte de ejecución del mantenimiento.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            onClick={() =>
              mutation.mutate({
                tipo,
                observaciones: observaciones.trim() || undefined,
                periodicidad_dias: Number(periodicidad) || undefined,
                pdf: pdfFile!, // sabemos que no es null por la validación
              })
            }
            disabled={mutation.isPending || !periodicidad || !pdfFile}
          >
            {mutation.isPending ? (
              <>
                <Loader2 className="animate-spin" />
                Registrando...
              </>
            ) : (
              'Registrar mantenimiento'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
